// frontend/src/i18n.ts
// Professional i18n configuration with JSON-based translations.

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import enTranslations from './locales/en.json';
import frTranslations from './locales/fr.json';
import enPricingAccess from './locales/pricingAccess/en.json';
import frPricingAccess from './locales/pricingAccess/fr.json';
import {
  DEFAULT_LANGUAGE,
  SUPPORTED_LANGUAGES,
  normalizeLanguageCode,
} from './locales/languages';

import logger from './utils/logger';

// Keep only English and French live until a deliberate auto-translation layer ships.
export const SUPPORTED_LANG_CODES = SUPPORTED_LANGUAGES.map(({ code }) => code);

const resources = {
  en: { translation: enTranslations, pricingAccess: enPricingAccess },
  fr: { translation: frTranslations, pricingAccess: frPricingAccess }
};

const syncLiveLanguage = (language: string) => {
  const liveLanguage = normalizeLanguageCode(language) || DEFAULT_LANGUAGE;

  if (typeof document !== 'undefined') {
    document.documentElement.lang = liveLanguage;
    document.documentElement.dir = 'ltr';
  }

  if (typeof window !== 'undefined') {
    try {
      window.localStorage.setItem('userLanguage', liveLanguage);
      window.localStorage.setItem('i18nextLng', liveLanguage);
    } catch {
      // Storage can be unavailable in privacy-restricted browsing contexts.
    }
  }

  logger.info('🌍 Language changed to: ' + liveLanguage);
};

i18n.on('languageChanged', syncLiveLanguage);

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: 'en',
    supportedLngs: SUPPORTED_LANG_CODES,
    load: 'languageOnly',
    cleanCode: true,
    
    detection: {
      order: ['querystring', 'cookie', 'localStorage', 'navigator', 'htmlTag'],
      lookupQuerystring: 'lang',
      lookupCookie: 'i18next',
      lookupLocalStorage: 'i18nextLng',
      caches: ['localStorage', 'cookie'],
      excludeCacheFor: ['cimode'],
      cookieMinutes: 525600,
    },
    
    interpolation: {
      escapeValue: false,
    },
    
    react: {
      useSuspense: false,
      bindI18n: 'languageChanged',
      bindI18nStore: '',
      transEmptyNodeValue: '',
      transSupportBasicHtmlNodes: true,
      transKeepBasicHtmlNodesFor: ['br', 'strong', 'i', 'p'],
    },
    
    returnNull: false,
    returnEmptyString: false,
    
    debug: process.env.NODE_ENV === 'development',
    
    keySeparator: false,
    nsSeparator: false,
  });

export default i18n;
